import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import { ContextOperation } from "../../../context/OperationContext";
import useOperationTranslation from "../../../hooks/useOperationTranslation";
import useSnackbar from "../../../hooks/useSnackbar";
import useToggle from "../../../hooks/useToggle";
import { saveFileFromResponse } from "../../../utils";
import { nameof } from "../../common/ObjectUtils";
import DamageFormService from "../service/DamageFormService";
import { DamageFormTypes } from "../Types";
import Dialogs from "./dialogs";
import { ContextDamageForm } from "../DamageFormContext";
import { useHasScope } from "../../../../../config/globalConfig";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import AgGridOperationTable from "../../common/AgGridOperationTable";

const PracticesTable = () => {
  const hasScope = useHasScope;
  const { tenant, setLoading } = useContext(ContextOperation);
  const damageFormContext = useContext(ContextDamageForm);
  const t = useOperationTranslation();
  const { globalData } = useContext(GlobalContext);
  const { id } = useParams();
  const snackbar = useSnackbar();

  const [openCreate, toggleCreate] = useToggle();

  const [data, setData] = useState<DamageFormTypes.IPractice[]>();

  const getPractices = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.generalInfo.practice.get
      .listById(tenant, id)
      .then((response) => {
        setData(response);
        setLoading(false);
      });
  };

  const getAttachments = () => {
    if (!id) return;
    DamageFormService.details.generalInfo.attachments.get
      .byId(id)
      .then((response) => {
        //setDataSource(response);
        damageFormContext.setAttachments(response);
      });
  };

  useEffect(() => {
    getPractices();
  }, []);

  const onDelete = (practiceId?: number) => {
    if (!practiceId) return;

    setLoading(true);
    DamageFormService.details.generalInfo.practice
      .delete(tenant, practiceId?.toString())
      .then(
        () => {
          snackbar.show(
            "success",
            t("damageForm.details.generalInfo.practice.snackbar.delete.success")
          );
          getPractices();
          getAttachments();
          setLoading(false);
        },
        () => {
          snackbar.show(
            "error",
            t("damageForm.details.generalInfo.practice.snackbar.delete.error")
          );
          setLoading(false);
        }
      );
  };

  const onCloseCreate = () => {
    getPractices();
    getAttachments();
    toggleCreate();
  };

  const onDownloadPractice = (practiceId?: number) => {
    if (!practiceId) return;

    setLoading(true);
    DamageFormService.details.generalInfo.practice.get
      .byId(tenant, practiceId)
      .then(
        (response) => {
          saveFileFromResponse(response);
          setLoading(false);
        },
        () => {
          snackbar.show(
            "error",
            t("damageForm.details.generalInfo.practice.snackbar.download.error")
          );
          setLoading(false);
        }
      );
  };

  const fieldFunction = () => {
    if (hasScope("SCOPE_UPDATE_DAMAGE_PRACTICE")) return "_actions";
    else return "";
  };

  const columns: ColDef[] = [
    {
      field: nameof<DamageFormTypes.IPractice>("practiceNumber"),
      headerName: t(
        "damageForm.details.generalInfo.practice.table.headers.practiceNumber"
      ),
      flex: 1,
    },
    {
      field: nameof<DamageFormTypes.IPractice>("repairShopSapId"),
      headerName: t(
        "damageForm.details.generalInfo.practice.table.headers.idRepairShop"
      ),
      flex: 1,
    },
    {
      field: nameof<DamageFormTypes.IPractice>("updateDate"),
      headerName: t(
        "damageForm.details.generalInfo.practice.table.headers.updateDate"
      ),
      flex: 1,
      cellRenderer: (
        params: ICellRendererParams<DamageFormTypes.IAttachments>
      ) => {
        return dayjs(params.value).format("DD/MM/YYYY HH:mm:ss");
      },
    },
  ];

  return (
    <>
      <Mui.Accordion>
        <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />}>
          {t("damageForm.details.generalInfo.practice.title")}
        </Mui.AccordionSummary>

        <Mui.AccordionDetails>
          <Mui.Card>
            <Mui.CardContent>
              
             <AgGridOperationTable
                columnDefs={columns}
                rowData={data}
                rowModelType="clientSide"
                totalElements={data?.length}
              />


            </Mui.CardContent>
          </Mui.Card>
        </Mui.AccordionDetails>
      </Mui.Accordion>

      <Dialogs.CreatePractice open={openCreate} onClose={onCloseCreate} />
    </>
  );
};

export default PracticesTable;
